import { flow } from '@helo/utils'

import { AuthSlice } from '../auth/authSlice'
import { PersistenceSlice } from './persistenceSlice'
import {
  initializeLocalstoragePersistenceKey,
  peristUrlStorageItems,
  persistLocalStorageItems,
  persistLocalstorageItem,
  persistenceItemsAreTheSame,
  retrieveFromLocalStorage,
  retrieveFromUrlStorage,
} from './persistenceSlice.parts'
import { PersistedItem } from './persistenceSlice.types'

const setPersistedItem = (item: PersistedItem) =>
  flow(
    flow.inputExtends<PersistenceSlice>(),
    <T extends PersistenceSlice>(store: T): T => {
      const firstIndex = store.persistence.items.findIndex(
        i => i.key === item.key,
      )
      const itemsExcludingThisKey = store.persistence.items.filter(
        i => i.key !== item.key,
      )
      const items =
        firstIndex === -1
          ? itemsExcludingThisKey.concat(item)
          : [
              ...itemsExcludingThisKey.slice(0, firstIndex),
              item,
              ...itemsExcludingThisKey.slice(firstIndex),
            ]

      return {
        ...store,
        persistence: {
          ...store.persistence,
          items,
        },
      }
    },
  )

const getPersistenceItem = <T = any>(key: string) =>
  flow(
    flow.inputExtends<PersistenceSlice>(),
    store =>
      store.persistence.items.find(i => i.key === key) as
        | PersistedItem<T>
        | undefined,
  )

const getPersistenceItemValue =
  <R>(key: string): (<T extends PersistenceSlice>(value: T) => R | undefined) =>
  store =>
    getPersistenceItem<R>(key)(store)?.value as R | undefined

const persistItem = (item: PersistedItem) =>
  flow(
    flow.inputExtends<PersistenceSlice>(),
    setPersistedItem(item),
    persistLocalstorageItem(item),
    // currently updates all items. Not sure if a singular one is worth it.
    // maybe helpful to do a diff to see if an update needs to occur.
    peristUrlStorageItems(),
  )

const persistItems = flow(
  flow.inputExtends<PersistenceSlice>(),
  persistLocalStorageItems(),
  peristUrlStorageItems(),
)

const initializePersistence = () =>
  flow(
    flow.inputExtends<PersistenceSlice & AuthSlice>(),
    <T extends PersistenceSlice & AuthSlice>(store: T): T =>
      initializeLocalstoragePersistenceKey(store.auth.user?.email!)(store),
    <T extends PersistenceSlice>(store: T): T => ({
      ...store,
      persistence: {
        ...store.persistence,
        initialized: true,
      },
    }),
  )

const loadPersistedItems = () =>
  flow(
    flow.inputExtends<PersistenceSlice>(),
    <T extends PersistenceSlice>(store: T): T => {
      const localStorageItems = retrieveFromLocalStorage(store.persistence)
      const urlStorageItems = retrieveFromUrlStorage()

      const items = [
        ...store.persistence.items,
        ...localStorageItems,
        ...urlStorageItems,
      ].reduceRight((acc, item) => {
        if (!acc.some(i => persistenceItemsAreTheSame(i, item))) {
          return [item, ...acc]
        }
        return acc
      }, [] as PersistedItem[])
      return {
        ...store,
        persistence: {
          ...store.persistence,
          items,
        },
      }
    },
  )

const triggerPersistenceEvent = (event: PersistedItem['until']) =>
  flow(
    flow.inputExtends<PersistenceSlice>(),
    <T extends PersistenceSlice>(store: T): T => ({
      ...store,
      persistence: {
        ...store.persistence,
        items: store.persistence.items.filter(item => item.until !== event),
      },
    }),
    persistLocalStorageItems(),
  )

export const PersistenceSliceActions = {
  setPersistedItem,
  getPersistenceItem,
  getPersistenceItemValue,
  persistItem,
  persistItems,
  initializePersistence,
  loadPersistedItems,
  triggerPersistenceEvent,
}
